import { useMemo } from 'react';

import { TabModules, useModulesConfig } from '~/data/company';
import { Modules } from '~/data/modules';
import { QuickLinkDto } from '~/services/Http/client.people.generated';
import { useGetEmployeeQuickLinksTemp } from '~/services/Http/hooks';

const customLocations = ['PEOP_NEW_EMPLOYEE', 'PEOP_NEW_EMPLOYEE_FORMS'];

export enum NavigatableModule {
  PEOPLE = 'PEOPLE',
  REWARDING = 'REWARDING',
  PERFORMANCE = 'PERFORMANCE',
  DEVELOPMENT = 'DEVELOPMENT',
  ANALYTICS = 'ANALYTICS',
  COLLABORATION = 'COLLABORATION',
  ORGANIZATION = 'ORGANIZATION',
  RECRUITING = 'RECRUITING',
  PLANNING = 'PLANNING',
  SF2 = 'SF2',
}

export enum SF2PathUrl {
  SF2_WORKSPACE = 'SF2_WORKSPACE',
  SF2_HOMEPAGE = 'SF2_HOMEPAGE',
}

export const getSF2Path = (sf2Path: string, includePrefix?: boolean) => {
  switch (sf2Path as SF2PathUrl) {
    case SF2PathUrl.SF2_WORKSPACE:
      return `${includePrefix ? '/v2' : ''}/my-workspace`;
    case SF2PathUrl.SF2_HOMEPAGE:
      return `${includePrefix ? '/v2' : ''}/home`;
    default:
      throw new Error(`${sf2Path} is not a supported menu link`);
  }
};

const sortLinks = (length: number) => (a: QuickLinkDto, b: QuickLinkDto) => {
  const aSeq = !a.seq && a.seq !== 0 ? length : a.seq;
  const bSeq = !b.seq && b.seq !== 0 ? length : b.seq;

  return aSeq - bSeq;
};

export type UseMenuLinksOutput = {
  generalMenuLinks: QuickLinkDto[];
};

export const useMenuLinks = (): UseMenuLinksOutput => {
  const { data: allGeneralQuickLinks } = useGetEmployeeQuickLinksTemp({
    full: true,
  });
  const { isModuleEnabled, isTabbedModule, getEnabledTabsForModule } =
    useModulesConfig();
  const generalMenuLinks = useMemo(() => {
    const isModuleAvailable = (navModule: string) => {
      if (navModule === NavigatableModule.RECRUITING) {
        return (
          isModuleEnabled(Modules.RECRUITING_FULL) ||
          isModuleEnabled(Modules.RECRUITING_MINI)
        );
      }

      if (navModule === NavigatableModule.ORGANIZATION) {
        return isModuleEnabled(Modules.ORGANISATION);
      }

      if (navModule === NavigatableModule.SF2) {
        return isModuleEnabled(Modules.UI2);
      }

      return isModuleEnabled(navModule);
    };

    const isTabAvailable = (
      navTab: string,
      enabledTabs: string[] | undefined,
    ) => {
      if (!enabledTabs || customLocations.includes(navTab)) {
        return true; // always allow custom tabs
      }

      return enabledTabs.includes(navTab);
    };

    const isMenuLinkAvailable = (ql: QuickLinkDto) => {
      if (ql.location !== 'menu_links' || !ql.navigation) {
        return false;
      }

      const { module, tab } = ql.navigation;
      const enabledModuleTabs = isTabbedModule(module)
        ? getEnabledTabsForModule(module as TabModules)
        : undefined;

      return (
        isModuleAvailable(module) && isTabAvailable(tab, enabledModuleTabs)
      );
    };

    const menuLinks = allGeneralQuickLinks
      ? allGeneralQuickLinks.filter(isMenuLinkAvailable)
      : [];

    return menuLinks.slice().sort(sortLinks(menuLinks.length));
  }, [
    allGeneralQuickLinks,
    isTabbedModule,
    isModuleEnabled,
    getEnabledTabsForModule,
  ]);

  return {
    generalMenuLinks,
  };
};
